import {LABEL_KEY} from '../../../model/label-key.model';
import {LabelTranslator} from '../../../model/label-translator.model';
import {LocalizedText} from '../../../model/localized-text.model';
import {EmailBlock} from './email-block.model';
import {EMAIL_BLOCK_TYPE} from './email-block-type.model';
import {EMAIL_TEMPLATE_TEXT_KEYS} from './email-template-text-keys.model';

const TEMPLATE_TEXT_KEYS: readonly string[] = EMAIL_TEMPLATE_TEXT_KEYS;

/**
 * Remplace, dans un bloc, la clé du gabarit par son texte.
 *
 * Le serveur crée l'e-mail à partir d'un gabarit dont les libellés sont des clés — le corps du
 * message, le libellé du bouton, le lien de désabonnement. Elles se traduisent à l'ouverture, une
 * fois pour toutes : le brouillon les renverrait sinon telles quelles au serveur, qui n'aurait de
 * l'e-mail qu'une suite de clés, illisible dans un envoi comme dans un aperçu.
 *
 * Un texte que l'utilisateur a écrit lui-même n'est pas une clé du gabarit et traverse inchangé.
 * Le bloc revient à l'identique quand il n'y a rien à traduire : c'est à cela que l'appelant
 * reconnaît qu'aucun enregistrement n'est nécessaire.
 */
export const translateEmailBlock = (block: EmailBlock, translate: LabelTranslator): EmailBlock => {
    // Une branche par type plutôt qu'un cas groupé : groupés, le type et ses options cessent d'être
    // corrélés — le compilateur ne voit plus qu'une union d'options, qu'aucun bloc ne satisfait.
    switch (block.type) {
        case EMAIL_BLOCK_TYPE.TEXT:
            return withTranslatedText(block, translate);

        case EMAIL_BLOCK_TYPE.BUTTON:
            return withTranslatedText(block, translate);

        case EMAIL_BLOCK_TYPE.UNSUBSCRIBE_LINK:
            return withTranslatedText(block, translate);

        default:
            return block;
    }
};

/** Le bloc, son libellé traduit s'il en portait une clé ; rendu à l'identique sinon. */
const withTranslatedText = <T extends {options: {text: LocalizedText}}>(block: T, translate: LabelTranslator): T => {
    const value = block.options.text[LABEL_KEY] ?? '';
    if (!TEMPLATE_TEXT_KEYS.includes(value)) return block;

    return {...block, options: {...block.options, text: {...block.options.text, [LABEL_KEY]: translate(value)}}};
};
